// A booking type's standing video link: the one check the World stores it by
// (lib/bookingOps.js normalizeMeetingUrl) and the form the approval card shows
// (APPS/cleo-code/apps/web/src/neucleos/gateInstancesClient.ts), so the card
// says exactly the link that will be stored and sent. Pure: no imports.
//
// The link is emailed under Neucleos's sending name to each booking's two
// parties, so it may only point at a known video-meeting service: an https
// address on one of these hosts or a subdomain of one (us02web.zoom.us,
// company.webex.com). Anything else refuses, even after approval, so a
// mistyped or injected address cannot reach a buyer.
export const MEETING_URL_MAX = 500;
/**
 * Where a booking type's standing meeting link goes, as every card says it: the Desk's meeting-link card
 * (gateInstancesClient.ts) and the consulting catalog card (lib/consultingCatalog.js) read these same words.
 */
export const MEETING_LINK_AUDIENCE_WORDS =
  "Emailed with every booking of this type to the person who booked and to you: the confirmation, a move, and the 24-hour and 1-hour reminders. Never shown on the public booking page.";
export const MEETING_URL_HOSTS = Object.freeze([
  "zoom.us", "meet.google.com", "teams.microsoft.com", "teams.live.com", "webex.com", "whereby.com",
]);

/** { url } in the canonical form stored and sent, or { problem } saying why it refuses. */
export function checkMeetingUrl(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (raw.length === 0 || raw.length > MEETING_URL_MAX) return { problem: `must contain 1-${MEETING_URL_MAX} characters.` };
  if (/[\s\\\u0000-\u001f\u007f]/.test(raw)) return { problem: "must not contain spaces, backslashes or control characters." };
  if (!/^https:\/\//i.test(raw)) return { problem: "must start with https://." };
  // Read as typed, before the parser forgives anything (extra slashes, a
  // default :443 port): exactly "https://" then the host, with no user name,
  // password or port of any kind.
  const authority = raw.slice("https://".length).split(/[/?#]/)[0];
  if (authority === "") return { problem: "must name its host right after https:// (no extra slashes)." };
  if (/[:@]/.test(authority)) return { problem: "must not carry a user name, password or port." };
  let url;
  try { url = new URL(raw); } catch { return { problem: "must be a complete https:// address." }; }
  if (url.protocol !== "https:") return { problem: "must start with https://." };
  if (url.username || url.password || url.port) return { problem: "must not carry a user name, password or port." };
  const host = url.hostname.toLowerCase();
  // The host the parser read is the host as written: nothing encoded or rewritten in between.
  if (host !== authority.toLowerCase()) return { problem: "must name its host plainly, as letters, digits, dots and hyphens." };
  if (!MEETING_URL_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))) {
    return { problem: `must be a video-meeting link on ${MEETING_URL_HOSTS.join(", ")} (or a subdomain of one).` };
  }
  if (url.href.length > MEETING_URL_MAX) return { problem: `must contain 1-${MEETING_URL_MAX} characters.` };
  return { url: url.href };
}
